import type { InstitutionCorrespondence, Attachment } from '../types';

const mockAttachments: Attachment[] = [
  {
    id: 'att-1',
    filename: 'Grant_of_Probate.pdf',
    size: 234567,
    mimeType: 'application/pdf',
    url: '/mock/attachments/grant-of-probate.pdf'
  },
  {
    id: 'att-2',
    filename: 'Death_Certificate.pdf',
    size: 156789,
    mimeType: 'application/pdf',
    url: '/mock/attachments/death-certificate.pdf'
  },
  {
    id: 'att-3',
    filename: 'Bank_Statement_Dec_2023.pdf',
    size: 89012,
    mimeType: 'application/pdf',
    url: '/mock/attachments/bank-statement.pdf'
  }
];

export function generateMockCorrespondence(
  count: number = 20,
  institutionId: number = 1
): InstitutionCorrespondence[] {
  const now = new Date();
  const correspondence: InstitutionCorrespondence[] = [];

  // Templates for different correspondence types
  const templates = {
    email: [
      {
        subject: 'Re: Estate closure request - Account ending 4567',
        content: `Thank you for submitting the estate closure request. We have received all required documents and are processing your request.

Expected completion time: 5-7 business days.

If you have any questions, please don't hesitate to contact us.`,
        hasAttachments: false
      },
      {
        subject: 'Additional documentation required',
        content: `We require the following additional documents to proceed with the account closure:

1. Original death certificate (not a copy)
2. Grant of probate
3. Completed indemnity form

Please upload these documents at your earliest convenience.`,
        hasAttachments: true,
        attachments: [mockAttachments[2]]
      },
      {
        subject: 'Account balance confirmation',
        content: `As requested, please find attached the final account balance statement for account ending 4567.

Balance as of closure date: £12,456.78

This amount will be transferred to the estate account within 3 business days.`,
        hasAttachments: true,
        attachments: [mockAttachments[2]]
      }
    ],
    phone: [
      {
        subject: 'Phone call with James Wilson',
        content: `Discussed account closure process. Client confirmed all beneficiaries have been notified. 

Agreed to send final documentation by end of week.

Follow up scheduled for next Tuesday.`,
        hasAttachments: false
      },
      {
        subject: 'Call regarding missing documentation',
        content: `Institution called to request missing indemnity form. Explained form was sent last week. 

They will check their records and call back if not found.`,
        hasAttachments: false
      }
    ],
    letter: [
      {
        subject: 'Notice of account closure completion',
        content: `We are writing to confirm that the account closure for the estate has been completed successfully.

All funds have been transferred as instructed.

Thank you for choosing our services.`,
        hasAttachments: true,
        attachments: [mockAttachments[0]]
      }
    ],
    portal_message: [
      {
        subject: 'New message in secure portal',
        content: `You have a new message waiting in our secure portal regarding your estate administration request.

Please log in to view and respond to this message.`,
        hasAttachments: false
      }
    ]
  };

  // Generate a conversation thread
  const threadCount = Math.ceil(count / 4); // Average 4 messages per thread

  for (let thread = 0; thread < threadCount; thread++) {
    const threadStartDate = new Date(
      now.getTime() - thread * 7 * 24 * 60 * 60 * 1000
    ); // Each thread starts 1 week apart
    const messagesInThread = Math.min(4, count - thread * 4);
    const threadSubject =
      templates.email[thread % templates.email.length].subject;

    for (let msg = 0; msg < messagesInThread; msg++) {
      const isOutbound = msg % 3 === 1; // Every 3rd message is outbound
      const type = msg === 0 ? 'email' : msg === 2 ? 'phone' : 'email';
      const templateGroup = templates[type as keyof typeof templates];
      const template = templateGroup[msg % templateGroup.length];

      const messageDate = new Date(
        threadStartDate.getTime() + msg * 2 * 60 * 60 * 1000
      ); // 2 hours between messages

      correspondence.push({
        id: `${institutionId}-${thread}-${msg}`,
        institutionId,
        matterId: 1,
        parentCorrespondenceId:
          msg > 0 ? `${institutionId}-${thread}-0` : undefined,
        subject: msg === 0 ? template.subject : `Re: ${threadSubject}`,
        content: isOutbound
          ? `Thank you for your message. ${template.content}`
          : template.content,
        type: type,
        direction: isOutbound ? 'outbound' : 'inbound',
        status: isOutbound ? 'sent' : 'received',
        sentAt: messageDate.toISOString(),
        attachments:
          template.hasAttachments && 'attachments' in template
            ? template.attachments
            : [],
        sender: isOutbound ? 'John Smith (Vale Legal)' : 'Customer Service',
        recipient: isOutbound ? 'Customer Service' : 'John Smith',
        threadId: `thread-${thread}`,
        createdAt: messageDate.toISOString(),
        updatedAt: messageDate.toISOString()
      });
    }
  }

  return correspondence.reverse(); // Newest first
}

// Generate mock data for different scenarios
export const mockCorrespondenceScenarios = {
  active: generateMockCorrespondence(15, 1),
  empty: [],
  singleThread: generateMockCorrespondence(4, 2).map((c) => ({
    ...c,
    threadId: 'thread-single'
  })),
  multipleInstitutions: [
    ...generateMockCorrespondence(8, 1),
    ...generateMockCorrespondence(6, 2),
    ...generateMockCorrespondence(4, 3)
  ],
  withDrafts: [
    ...generateMockCorrespondence(10, 1),
    {
      id: 'draft-1',
      institutionId: 1,
      matterId: 1,
      subject: 'Draft: Follow up on account closure',
      content: `I wanted to follow up on the account closure request submitted last week.

We have provided all the required documentation including:
- Grant of Probate
- Death Certificate
- Executor identification

Could you please confirm the current status and expected timeline for completion?`,
      type: 'notification' as const,
      direction: 'outbound' as const,
      status: 'draft' as const,
      sentAt: new Date().toISOString(),
      attachments: [],
      sender: 'John Smith (Vale Legal)',
      recipient: 'Customer Service',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: 'draft-2',
      institutionId: 1,
      matterId: 1,
      subject: 'Draft: Missing beneficiary information',
      content: `We noticed that the beneficiary information for one of the accounts appears to be incomplete.

The account ending in 4567 shows a beneficiary but no percentage allocation. Could you please clarify:

1. Is this the only beneficiary for this account?
2. Should they receive 100% of the funds?
3. Are there any other beneficiaries we should be aware of?

This information is crucial for proper distribution of the estate.`,
      type: 'notification' as const,
      direction: 'outbound' as const,
      status: 'draft' as const,
      sentAt: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
      attachments: [],
      sender: 'Sarah Johnson (Vale Legal)',
      recipient: 'Estate Department',
      createdAt: new Date(Date.now() - 3600000).toISOString(),
      updatedAt: new Date(Date.now() - 1800000).toISOString() // Updated 30 min ago
    },
    {
      id: 'draft-3',
      institutionId: 1,
      matterId: 1,
      parentCorrespondenceId: 'draft-1',
      subject: 'Draft: Urgent - Estate closure deadline approaching',
      content: `URGENT: We need to complete the estate closure by month-end due to tax filing requirements.

Please expedite the processing of all pending items. We are available for a call this week if that would help resolve any outstanding issues.

Available times:
- Tuesday 2-4 PM
- Wednesday 10 AM - 12 PM  
- Thursday any time

Please let us know how we can accelerate this process.`,
      type: 'notification' as const,
      direction: 'outbound' as const,
      status: 'draft' as const,
      sentAt: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
      attachments: [],
      sender: 'John Smith (Vale Legal)',
      recipient: 'Customer Service',
      createdAt: new Date(Date.now() - 7200000).toISOString(),
      updatedAt: new Date(Date.now() - 600000).toISOString() // Updated 10 min ago
    }
  ]
};
